import React from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';
import type { ContactRequestResponse } from '../../api/viewContactRequestApiService';
import { getFileIcon } from './attachmentIcon';

const SubLabel: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-bodySmall font-normal text-on-surface-variant">{text}</p>
);

/**
 * "Response" tab — the assigned admin's completion attachments and free-text
 * note, read back from `detail.response` (RR-1096). The backend does not yet
 * populate this (a future feature), so an absent object, null fields, and empty
 * arrays all render as the empty placeholder. Shown only on the
 * my-assigned-task view.
 */
const CRResponseTab: React.FC<{
  response: ContactRequestResponse | undefined;
}> = ({ response }) => {
  const attachments = (response?.attachments ?? []).filter((a) =>
    Boolean(a.url)
  );
  const notes = response?.notes?.trim() ?? '';
  const hasResponse = attachments.length > 0 || notes.length > 0;

  return (
    <div
      className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4 flex-1 min-h-0"
      data-testid="view-cr-response-section"
    >
      <p className="text-titleDefault font-medium text-on-surface-variant">
        {L.VIEW_CR_NAV_RESPONSE}
      </p>

      {!hasResponse ? (
        <p
          className="px-5 py-3.5 text-center text-sm font-normal leading-5 tracking-[0.25px] text-on-surface-variant"
          data-testid="view-cr-response-empty"
        >
          {L.VIEW_CR_RESPONSE_EMPTY_STATE}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <SubLabel text={L.VIEW_CR_RESPONSE_ATTACHMENTS} />
            {attachments.length > 0 ? (
              <div className="flex flex-col gap-4">
                {attachments.map((file, idx) => (
                  <div
                    key={`${file.filename}-${idx}`}
                    className="flex items-center justify-between gap-3"
                    data-testid={`view-cr-response-attachment-${idx}`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      {getFileIcon(file.filename)}
                      <span className="text-sm text-[#181A1B] break-all">
                        {file.filename}
                      </span>
                    </span>
                    <a
                      href={file.url ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      download={file.filename}
                      className="p-2 rounded-full cursor-pointer hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] text-[#181A1B] shrink-0"
                      aria-label={`Pobierz ${file.filename}`}
                      data-testid={`view-cr-response-attachment-${idx}-download`}
                    >
                      <ArrowDownTrayIcon className="w-5 h-5" />
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p
                className="text-sm text-[#181A1B]"
                data-testid="view-cr-response-attachments-empty"
              >
                {L.VIEW_CR_RESPONSE_EMPTY}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <SubLabel text={L.VIEW_CR_RESPONSE_NOTES} />
            <p
              className="text-sm text-[16px] text-[#181A1B] whitespace-pre-wrap break-words"
              data-testid="view-cr-response-notes-value"
            >
              {notes.length > 0 ? notes : L.VIEW_CR_RESPONSE_EMPTY}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default CRResponseTab;
